import React, {
  ReactElement,
  ReactNode,
  useState,
  isValidElement,
} from 'react';
import cn from 'classnames';
import Link from 'next/link';
import { pickBy } from 'lodash-es';
import { SelectionT } from '@/lib/collections/catalogue/helpers';
import HotDealBadge from '../hotDeals/HotDealBadge';
import { Tooltip } from './ColorVariant';

type InputAttributes = React.AnchorHTMLAttributes<HTMLAnchorElement>;

const ANCHOR_BASE =
  'h-full w-full flex items-center justify-center leading-3 cursor-pointer py-4 lg:py-3 bg-white text-center text-[10px] sm:text-xs border hover:border-gray-700 rounded relative';
const ANCHOR_INACTIVE = 'border-slate-gray-200 text-gray-700';
const ANCHOR_ACTIVE =
  'bg-white text-gray-700 !border-gray-700 !lg:border-gray-700 font-bold';
const ANCHOR_SUGGESTED = 'text-gray-400 border-gray-900 border-dashed border-1';
const ANCHOR_HOVERD = 'text-gray-700 border-gray-400 border-dashed border-1';
const ANCHOR_DISABLED = 'opacity-30 pointer-events-none';

type QueryResult =
  | null
  | (SelectionT & { replacedFromCondition?: string; suggested?: boolean });

type Query = QueryResult;

function Anchor(
  props: InputAttributes & {
    query: Query;
    active: boolean | string;
    isDeal?: boolean;
    isConditionVariant?: boolean;
    isPremiumCondition?: boolean;
    disabled?: boolean;
    offerCondition?: string;
    variant: string;
    setSuggestedVariant: React.Dispatch<React.SetStateAction<string>>;
  },
) {
  const {
    className,
    children,
    query,
    active,
    isDeal = false,
    isConditionVariant = false,
    isPremiumCondition = false,
    disabled = false,
    offerCondition,
    variant,
    setSuggestedVariant,
    ...linkProps
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const suggested = typeof query === 'function' ? null : query?.suggested;
  const isOfferConditionDisabled =
    typeof query === 'function'
      ? null
      : offerCondition && query?.condition !== offerCondition;

  const updatedQuery = pickBy(query, (value, key) => key !== 'suggested');

  return (
    <>
      <div
        className={`${
          (query == null || disabled || !query) && `cursor-not-allowed`
        }`}
        onMouseEnter={() => {
          if (suggested) setIsOpen(true);
        }}
        onMouseLeave={() => setIsOpen(false)}
        onClick={() => {
          setIsOpen(false);
          if (suggested) setSuggestedVariant(variant);
          if (!suggested) setSuggestedVariant('');
        }}
      >
        <Link
          href={{
            pathname: '/collections/[handle]',
            query: updatedQuery,
          }}
          scroll={false}
          shallow
        >
          <a
            className={cn(
              ANCHOR_BASE,
              {
                [ANCHOR_DISABLED]:
                  (disabled || query == null) && isOfferConditionDisabled,
                [ANCHOR_SUGGESTED]:
                  !disabled && query != null && !isOpen && suggested,
                [ANCHOR_HOVERD]:
                  !disabled && query != null && isOpen && suggested,
                [ANCHOR_INACTIVE]:
                  !disabled && query != null && !active && !suggested,
                [ANCHOR_ACTIVE]: active && !disabled && !suggested,
                '!items-start !px-2 xs:!px-3': isConditionVariant,
                '!text-white': isPremiumCondition && active && !suggested,
              },
              className,
            )}
            // eslint-disable-next-line react/forbid-dom-props
            style={
              isPremiumCondition && active && !suggested
                ? {
                    background:
                      'linear-gradient(285.62deg, #13505B 7.46%, #232222 91.74%)',
                  }
                : undefined
            }
            data-active={active && !disabled && !suggested}
            {...linkProps}
          >
            {isOpen && <Tooltip />}

            {isDeal && <HotDealBadge alwaysCenter isPremiumCondition />}
            {children}
          </a>
        </Link>
      </div>
    </>
  );
}

function Group(props: {
  id: string;
  title: ReactNode | string;
  psku: string;
  children?: ReactElement[];
  className: string;
  isDeal?: boolean;
  suggestedVariant: string;
}) {
  if (props.children == null || props.children?.length === 0) return null;

  const shouldShowUpdatedText =
    typeof props.title === 'string'
      ? props.suggestedVariant === props.title?.toString().toLowerCase()
      : isValidElement(props.title) &&
        props.suggestedVariant === props.title.props.children[0].toLowerCase();

  return (
    <div className="mb-4">
      <h3 className="mb-1 flex text-[10px] font-bold uppercase text-gray-700 lg:font-normal">
        {props.title}
      </h3>
      <div
        key={props.psku}
        className={cn(
          'gap-2',
          {
            'mt-2.5': props.isDeal,
          },
          props.className,
        )}
        id={props.id}
      >
        {props.children}
      </div>
      {shouldShowUpdatedText && (
        <span className="mb-3 text-xs text-[#1C6C49]">
          Updated other options based on this selection
        </span>
      )}
    </div>
  );
}

export default { Anchor, Group };
